import React, { useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { HiX, HiChevronLeft, HiChevronRight } from "react-icons/hi";

type ArtworkLightboxShellProps = {
  ariaLabel: string;
  imageUrl: string;
  imageAlt: string;
  onClose: () => void;
  onNavigate?: (direction: -1 | 1) => void;
  showNavigation?: boolean;
  imageIndex?: number;
  imageCount?: number;
  onImageNavigate?: (direction: -1 | 1) => void;
  imageFooter?: ReactNode;
  children: ReactNode;
};

export function ArtworkLightboxShell({
  ariaLabel,
  imageUrl,
  imageAlt,
  onClose,
  onNavigate,
  showNavigation = true,
  imageIndex = 0,
  imageCount = 1,
  onImageNavigate,
  imageFooter,
  children,
}: ArtworkLightboxShellProps) {
  const { t } = useTranslation();
  const hasImageNav = imageCount > 1 && Boolean(onImageNavigate);

  const navigateArtwork = useCallback(
    (direction: -1 | 1) => onNavigate?.(direction),
    [onNavigate],
  );

  const navigateImage = useCallback(
    (direction: -1 | 1) => onImageNavigate?.(direction),
    [onImageNavigate],
  );

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") {
        if (hasImageNav) navigateImage(-1);
        else if (onNavigate) navigateArtwork(-1);
      }
      if (event.key === "ArrowRight") {
        if (hasImageNav) navigateImage(1);
        else if (onNavigate) navigateArtwork(1);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose, hasImageNav, navigateImage, navigateArtwork, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-canvas-950/90 p-0 backdrop-blur-sm animate-fade-in sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute end-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20 sm:end-5 sm:top-5"
        aria-label={t("common.close")}
      >
        <HiX className="h-6 w-6" />
      </button>

      {showNavigation && onNavigate && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigateArtwork(-1);
            }}
            className="absolute start-2 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20 sm:flex sm:start-4"
            aria-label={t("common.prev")}
          >
            <HiChevronLeft className="h-6 w-6 rtl:rotate-180" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigateArtwork(1);
            }}
            className="absolute end-2 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20 sm:flex sm:end-4"
            aria-label={t("common.next")}
          >
            <HiChevronRight className="h-6 w-6 rtl:rotate-180" />
          </button>
        </>
      )}

      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl animate-slide-up sm:max-h-[90vh] sm:rounded-2xl sm:animate-scale-in md:flex-row"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 md:w-3/5">
          <div className="relative flex min-h-[14rem] w-full items-center justify-center bg-gradient-to-b from-canvas-50 to-canvas-100 sm:min-h-[18rem] md:min-h-[28rem]">
            <div className="relative h-[min(48vh,18rem)] w-full sm:h-[min(52vh,24rem)] md:h-[min(72vh,34rem)]">
              <img
                key={imageUrl}
                src={imageUrl}
                alt={imageAlt}
                decoding="async"
                className="absolute inset-0 z-[1] h-full w-full object-contain p-3 sm:p-4"
              />

            {hasImageNav && (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigateImage(-1);
                  }}
                  aria-label={t("common.prev")}
                  className="absolute start-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/90 text-canvas-800 shadow-md ring-1 ring-hungru-gold/25 transition hover:bg-white hover:ring-hungru-gold/40 sm:h-10 sm:w-10"
                >
                  <HiChevronLeft className="h-4 w-4 rtl:rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigateImage(1);
                  }}
                  aria-label={t("common.next")}
                  className="absolute end-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/90 text-canvas-800 shadow-md ring-1 ring-hungru-gold/25 transition hover:bg-white hover:ring-hungru-gold/40 sm:h-10 sm:w-10"
                >
                  <HiChevronRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                <span className="absolute end-2 top-2 z-10 rounded-full bg-canvas-950/60 px-2.5 py-0.5 text-[10px] font-medium text-white sm:end-3 sm:top-3">
                  {imageIndex + 1} / {imageCount}
                </span>
              </>
            )}
            </div>
          </div>
          {imageFooter}
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-6 md:w-2/5 md:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
